import { BookingEngine, Rates, roomData } from "../../index";
import Selectors from "./Selectors";
import waitForSelectors from "../../utilities/waitForSelectors";
import spaUrlChanged from "../../utilities/spaUrlChanged";
import { handleIntegrationInjector } from "../../hotelIntegrationHandlers";
import axios from "axios";
import {
  guestbookSites,
  environment,
} from "../../../settings/IntegrationConstants";
import moment from "moment";
import {
  setNativeValue,
  getCurrencySymbol,
  parsePrice,
  getBrowserInfo,
  getDeviceInfo,
  getUrlParameter,
} from "../../utilities/utils";

declare global {
  interface Window {
    gb_data: any;
    _globalTracking: any;
    angular: any;
  }
}

const gbData = window.gb_data;

const CloudbedsV3: BookingEngine = {
  name: gbData.bookingEngine,
  hotel: {
    name: gbData.hotel.name,
    isAutomated: gbData.hotel.isAutomated,
    rateCodeExclusions: gbData.hotel.rateCodeExclusions,
    packageRateExlusions: gbData.hotel.packageRateExlusions,
    exludeAllGroupCodes: gbData.hotel.exludeAllGroupCodes,
    exludeAllCorpCodes: gbData.hotel.exludeAllCorpCodes,
    whiteLabel: gbData.hotel.whiteLabel,
    dontAbTrack: true,
    memberCode: gbData.hotel.memberCode,
    memberCodeType: gbData.hotel.memberCodeType,
    showEnrollment: JSON.parse(
      sessionStorage.getItem("gbShowEnrollment") || "true"
    ),
    widgetIdentifier: gbData.widget.identifier,
    primaryColor: gbData.widget.primaryColor,
    secondaryColor: gbData.widget.secondaryColor,
    widgetAnchored: gbData.widget.widgetAnchored,
    widgetWidth: gbData.widget.widgetWidth,
    widgetPositionTop: gbData.widget.widgetPositionTop,
    widgetPositionRight: gbData.widget.widgetPositionRight,
    widgetPositionBottom: gbData.widget.widgetPositionBottom,
    widgetPositionLeft: gbData.widget.widgetPositionLeft,
    inheritBeFont: gbData.widget.inheritBeFont,
    desktopEnrollmentPrimaryColor: gbData.widget.desktopEnrollmentPrimaryColor,
    benefits: gbData.widget.benefits,
  },
  selectors: Selectors,
  singlePageApp: true,
  requireResizeListener: true,
  widgetFixedPosition: !gbData.widget.widgetAnchored,
  widgetMobileAtWidth: 961,
  largeEnrollmentMessage: false,
  activeIntervals: [],
  promoCodes: [],

  /* ADDITIONAL NOTES */
  // can use comment // @ts-ignore to ignore out ts errors

  /**
   * Grab widget target
   * (not usually modified)
   */
  getWidgetTarget: () =>
    document.querySelector(CloudbedsV3.selectors.widget.waitFor),
  /**
   * Applies member code to room search at user login
   * Only applicable if we have an existing member code (ExampleBookingEngine.hotel.memberCode)
   * Reference Travelclick.ts or SynxisGTM.ts for fleshed out example
   */
  getMemberCode: () => {
    // ...
  },
  /**
   * Clears out member code at user logout
   */
  clearMemberCode: () => {
    // ...
  },
  /**
   * Grab rates info and target to inject, Also applies rate exclusions if any exist
   * @returns ratesInfo[{rewardsAmount, shouldDisplay, targetElement},{},...]
   *
   * select all relevant rate $XX.XX elements, parse out the price,
   * check for exclusion if needed and, determine where to inject our messaging (messageTarget)
   */
  getRates: () => {
    const ratesInfo: Rates[] = [];
    const rates = document.querySelectorAll(
      CloudbedsV3.selectors.ratesPage.rates
    );

    rates.forEach((rate: any, index: number) => {
      const rateElement = rate as HTMLElement;
      const rateParent = rateElement.parentElement;

      if (rateParent) {
        const messageTarget = rateParent.parentElement;
        let injectedElement: HTMLElement | null = null;
        if (messageTarget) {
          const messageSelector = CloudbedsV3.selectors.ratesPage.ratesMessage;

          const existingMessageElement = messageTarget.querySelector(
            messageSelector
          );

          injectedElement =
            existingMessageElement || document.createElement("div");

          if (!existingMessageElement && injectedElement) {
            const className = messageSelector.startsWith(".")
              ? messageSelector.slice(1)
              : messageSelector;
            injectedElement.setAttribute("class", className);

            messageTarget.insertBefore(injectedElement, rateParent.nextSibling);
          }
        }

        const taxedRate = window.gb_data.hotel.taxedRate;
        const ratePrice = parsePrice((rate as HTMLElement).innerText);
        let shouldDisplay = true;
        let calculatedRewards = (ratePrice / taxedRate) * 0.15;

        const rateCodes = CloudbedsV3.getRateTypes
          ? CloudbedsV3.getRateTypes()
          : [];
        const packageRateExlusions = CloudbedsV3.hotel.packageRateExlusions;

        if (packageRateExlusions && rateCodes.length === rates.length) {
          packageRateExlusions.forEach((packageRateExlusion: any) => {
            if (packageRateExlusion.name === rateCodes[index]) {
              if (packageRateExlusion.rate === 0) shouldDisplay = false;
              else {
                calculatedRewards =
                  (ratePrice - packageRateExlusion.rate / taxedRate) * 0.15;
              }
            }
          });
        }

        ratesInfo.push({
          rewardsAmount: (calculatedRewards as any).toFixed(0),
          shouldDisplay: shouldDisplay,
          target: injectedElement,
        });
      }
    });
    sessionStorage.setItem("rates", JSON.stringify(ratesInfo));
    return ratesInfo;
  },
  /**
   * Grab the confirmation numbers on the final booking page
   * @returns confimationNumbers[], array used to account for multiple room bookings
   */
  getConfirmationNumber: () => {
    const dataLayerConfirmationNumber = window.dataLayer.filter(
      (res) => res[1] === "conversion"
    )[0];
    if (
      dataLayerConfirmationNumber?.length &&
      dataLayerConfirmationNumber[2]?.transaction_id
    ) {
      const confirmationNumber = dataLayerConfirmationNumber[2]?.transaction_id;
      return [confirmationNumber];
    }

    // backup selectors incase dataLayer fails
    const confirmationNumber = document
      .querySelector(CloudbedsV3.selectors.confirmationPage.confirmationNumbers)
      .textContent.split(" ")
      .at(-1)
      .replace("#", "");

    return [confirmationNumber];
  },
  /**
   * Grab the confirmation message target
   * (where to insert our confirmation message)
   */
  getConfirmationTarget: () =>
    document.querySelector(
      CloudbedsV3.selectors.confirmationPage.confirmationMessageContainer
    ),
  /**
   * Grab displayed rates page currency symbol
   * Try to use window variables, url params, or scrape
   * can use:
   *    .textContent.trim().replace(/[\d\., ]/g, '');
   *    if we need to parse out price
   */
  getRatesCurrency: () => {
    return getCurrencySymbol(
      document.querySelector(CloudbedsV3.selectors.widget.currency)?.textContent
    );
  },
  /**
   * Grab displayed checkout page currency symbol
   * Try to use window variables, url params, or scrape
   * can use:
   *  .textContent.trim().replace(/[\d\., ]/g, '');
   * if we need to parse out price
   */
  getCheckoutCurrency: () => {
    if (
      !!!document.querySelector(CloudbedsV3.selectors.widget.currency) ||
      !!document.querySelector(CloudbedsV3.selectors.confirmationPage.waitFor)
    ) {
      return sessionStorage.checkoutCurrency;
    }

    const checkoutCurrency = getCurrencySymbol(
      document.querySelector(CloudbedsV3.selectors.widget.currency)?.textContent
    );

    sessionStorage.setItem("checkoutCurrency", checkoutCurrency);
    return checkoutCurrency;
  },
  /**
   * Grab currency CODE to save in reservation payload
   * Try to use window variables, url params, or scrape
   */
  getCurrencyToSave: () => {
    return document.querySelector(CloudbedsV3.selectors.paymentPage.currency)
      .textContent;
  },
  /**
   * Grab language code
   */
  getLanguage: () => {
    const targetElement = document.querySelector("html") as HTMLElement;
    const lang = targetElement.getAttribute("lang")?.split("-")[0];
    if (typeof lang === "string") {
      return lang;
    } else {
      return "en";
    }
  },
  /**
   * Grab our enrollment message container
   * (not usually modified)
   */
  getEnrollmentTarget: () =>
    document.querySelector(
      CloudbedsV3.selectors.paymentPage.enrollmentMessageContainer
    ),
  /**
   * Grab reservation check in for reservation payload
   */
  getPaymentPageCheckinDate: () => {
    let checkInText = document
      .querySelector(CloudbedsV3.selectors.paymentPage.checkin)
      .textContent.trim();
    let checkIn = moment(new Date(checkInText)).format("YYYY-MM-DD");
    return checkIn;
  },
  /**
   * Grab reservation check in for reservation payload
   */
  getPaymentPageCheckoutDate: () => {
    let checkOutText = document
      .querySelector(CloudbedsV3.selectors.paymentPage.checkout)
      .textContent.trim();
    let checkIn = moment(new Date(checkOutText)).format("YYYY-MM-DD");
    return checkIn;
  },
  /**
   * Prefill user info on payment page if user logged in
   * (not usually modified)
   */
  prefillUserInfo: (firstName = "", lastName = "", email = "") => {
    waitForSelectors(CloudbedsV3.selectors.paymentPage.waitFor, () => {
      const emailInput = document.querySelector(
        CloudbedsV3.selectors.paymentPage.email
      );
      const firstNameInput = document.querySelector(
        CloudbedsV3.selectors.paymentPage.firstname
      );
      const lastNameInput = document.querySelector(
        CloudbedsV3.selectors.paymentPage.lastname
      );
      const event = new Event("input", { bubbles: true });

      setNativeValue(firstNameInput, firstName);
      firstNameInput.focus();
      firstNameInput.dispatchEvent(event);
      firstNameInput.blur();

      setNativeValue(lastNameInput, lastName);
      lastNameInput.focus();
      lastNameInput.dispatchEvent(event);
      lastNameInput.blur();

      setNativeValue(emailInput, email);
      emailInput.focus();
      emailInput.dispatchEvent(event);
      emailInput.blur();

      spaUrlChanged(() => {
        CloudbedsV3.prefillUserInfo(firstName, lastName, email);
      });
    });
  },
  /**
   * Grab email from payment page input
   * (not usually modified)
   */
  getGuestEmail: () =>
    document
      .querySelector(CloudbedsV3.selectors.paymentPage.email)
      .value.trim(),
  /**
   * Grab first name from payment page input
   * (not usually modified)
   */
  getGuestFirstName: () =>
    document
      .querySelector(CloudbedsV3.selectors.paymentPage.firstname)
      .value.trim(),
  /**
   * Grab last name from payment page input
   * (not usually modified)
   */
  getGuestLastName: () =>
    document
      .querySelector(CloudbedsV3.selectors.paymentPage.lastname)
      .value.trim(),
  /**
   * Grab rate TYPE from payment page cart
   * Should be a name like "Best Available" if possible and not just "BAR"
   */
  getRateType: () => {
    let rateNames: string[] = [];
    let rates = document.querySelectorAll(
      CloudbedsV3.selectors.paymentPage.rateNames
    );
    rates.forEach((item: any) => {
      rateNames.push(item.textContent);
    });

    return rateNames.join(";");
  },
  /**
   * Grab room name for reservation payload
   */
  getRoomName: () => {
    let roomNames: string[] = [];
    let rooms = document.querySelectorAll(
      CloudbedsV3.selectors.paymentPage.roomNames
    );
    rooms.forEach((item: any) => {
      roomNames.push(item.textContent);
    });

    return roomNames.join(";");
  },
  /**
   * Grab number of adults, used in package rate exclusion calculations
   */
  getNumberOfAdults: () => {
    let adults = document.querySelector(
      CloudbedsV3.selectors.paymentPage.adults
    );
    return adults;
  },
  /**
   * Grab number of days, used in package rate exclusion calculations
   */
  getNumberOfDays: () => {
    let days = document.querySelector(CloudbedsV3.selectors.paymentPage.adults);
    return days;
  },
  /**
   * Grab list of rate codes, used in package rate exclusion calculations
   */
  getRateTypes: () => {
    let rateNames: string[] = [];
    let rates = document.querySelectorAll(
      CloudbedsV3.selectors.ratesPage.ratesCodes
    );
    rates.forEach((item: any) => {
      rateNames.push(item.textContent.trim());
    });
    return rateNames;
  },
  /**
   * Multiple use cases
   * 1) Grab all promo/group codes applied
   * If multiple can be applied at once, store them all to ExampleBookingEngine.promoCodes
   * then retrieve them in getAllPromoCodes
   *
   * 2) just return the one code if distincy
   */
  getPromoCode: () => {
    let promoCode = getUrlParameter("promo");
    return promoCode;
  },
  getGroupCode: () => {
    let groupCode = "";
    // return document.querySelector(ExampleBookingEngine.selectors.paymentPage.groupCode).innerText;
    return groupCode;
  },
  getAllPromoCodes: () => {
    return CloudbedsV3.promoCodes ? CloudbedsV3.promoCodes.join(", ") : "";
  },
  /**
   * Grab subtotal at checkout to save
   */
  getSubtotal: () => {
    let subtotal = 0;
    document
      .querySelectorAll(CloudbedsV3.selectors.paymentPage.subtotal)
      .forEach((ele) => {
        subtotal += parsePrice(ele.textContent);
      });

    return Number((subtotal / window.gb_data.hotel.taxedRate).toFixed(2))
  },
  /**
   * Grab grand total with taxes and fees at checkout to save
   */
  getTotalWithTaxes: () => {
    const total = document.querySelector(
      CloudbedsV3.selectors.paymentPage.total
    ).textContent;
    return parsePrice(total);
  },
  /**
   * Save reservation data
   */
  getReservationData: () => {
    let resData: roomData[] = [];
    const promoCode = getUrlParameter("promo");
    if (CloudbedsV3.isRateExcluded(promoCode)) {
      return resData;
    }

    resData.push({
      email: CloudbedsV3.getGuestEmail(),
      first_name: CloudbedsV3.getGuestFirstName(),
      last_name: CloudbedsV3.getGuestLastName(),
      confirmation_number: "",
      checkin_date: CloudbedsV3.getPaymentPageCheckinDate(),
      checkout_date: CloudbedsV3.getPaymentPageCheckoutDate(),
      rate_type: CloudbedsV3.getRateType() as string,
      room_name: CloudbedsV3.getRoomName() as string,
      currency_code: CloudbedsV3.getCurrencyToSave(),
      lang_code: CloudbedsV3.getLanguage(),
      total: (CloudbedsV3.getSubtotal() as number[]).toString(),
      total_with_taxes: (CloudbedsV3.getTotalWithTaxes() as number[]).toString(),
      hotel_identifier: window.gb_data.widget.identifier,
      agreed_enrollment_checked: sessionStorage.getItem(
        "gbEnrolledChecked"
      ) as string,
      ab_version_viewed: sessionStorage.getItem("gbShowEnrollment") as string,
      browser_info: getBrowserInfo(),
      is_mobile: getDeviceInfo(),
      promo_code: CloudbedsV3.getAllPromoCodes
        ? CloudbedsV3.getAllPromoCodes()
        : "",
    });

    return resData;
  },
  /**
   * Used by Widget at checkout, EnrollmentMessage, and ConfirmationMessage
   * @returns rewards amount
   */
  getRewards: (percentage: number) => {
    let calculatedRewards = 0;
    // Simple cases (single subtotal, no exclusions), use displaySubtotal
    let subtotal = CloudbedsV3.displaySubtotal();
    calculatedRewards = Number((subtotal * percentage).toFixed(2));
    return calculatedRewards.toFixed(2);
  },
  /**
   * checks if messaging should be displayed
   * cross check passed in rate with list of package rate exclusions
   * @param rate: name of rate to match with package exclusion name
   * @returns true or false if rate is excluded
   */
  isRateExcluded: (rate: string) => {
    const packageRateExlusions = CloudbedsV3.hotel.packageRateExlusions;
    for (const packageRateExlusion of packageRateExlusions as any) {
      if (rate === packageRateExlusion.name) {
        return true;
      }
    }
    return false;
  },
  /**
   * gets the rate exclusion matching the rate code
   * cross check passed in rate with list of package rate exclusions
   * @param rate: name of rate to match with package exclusion name
   * @returns the packageRateExlusion object associated with the passed in rate name
   */
  getRateExcluded: (rate: string) => {
    const packageRateExlusions = CloudbedsV3.hotel.packageRateExlusions;
    for (const packageRateExlusion of packageRateExlusions as any) {
      if (rate === packageRateExlusion.name) {
        return packageRateExlusion;
      }
    }
  },
  /**
   * Checks if messaging should be displayed
   * sets rateExcluded in sessionStorage accordingly
   * @returns
   */
  shouldDisplayMessaging: () => {
    sessionStorage.setItem("rateExcluded", "false");
    // if show enrollment (from AB is set to false)
    if (!JSON.parse(sessionStorage.getItem("gbShowEnrollment") || "true"))
      return false;

    const promoCode = CloudbedsV3.getPromoCode()
      ? CloudbedsV3.getPromoCode()
      : "";
    const groupCode = CloudbedsV3.getGroupCode()
      ? CloudbedsV3.getGroupCode()
      : "";
    const rateCodeExclusions = CloudbedsV3.hotel.rateCodeExclusions;
    // blanket exclusions
    if (CloudbedsV3.hotel.excludeAllPromoCodes) {
      if (promoCode) {
        sessionStorage.setItem("rateExcluded", "true");
        return false;
      }
    } else if (CloudbedsV3.hotel.exludeAllGroupCodes) {
      if (groupCode) {
        sessionStorage.setItem("rateExcluded", "true");
        return false;
      }
    }

    for (const rateCodeExclusion of rateCodeExclusions as any) {
      // Do not show if match exists
      if (
        rateCodeExclusion.rate_plan_code.toLowerCase() ===
        promoCode.toLowerCase() ||
        rateCodeExclusion.rate_plan_code.toLowerCase() ===
        groupCode.toLowerCase()
      ) {
        sessionStorage.setItem("rateExcluded", "true");
        return false;
      }
    }

    // show messaging if all checks pass
    return true;
  },
  /** Uses waitUntilExists to check if the user should see widget
   * @param handler
   * (not usually modified)
   */
  addWidgetListener: (handler) => {
    const timeout = waitForSelectors(
      CloudbedsV3.selectors.widget.waitFor,
      handler
    );
    if (CloudbedsV3.activeIntervals) {
      CloudbedsV3.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on rates page then executes the passed handler function
   * @param handler
   * (not usually modified)
   */
  addRatesPageListener: (handler) => {
    const timeout = waitForSelectors(
      CloudbedsV3.selectors.ratesPage.waitFor,
      handler
    );
    if (CloudbedsV3.activeIntervals) {
      CloudbedsV3.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on payment page then executes the passed handler function
   * @param handler
   * (not usually modified)
   */
  addPaymentPageListener: (handler) => {
    const timeout = waitForSelectors(
      CloudbedsV3.selectors.paymentPage.waitFor,
      handler
    );
    if (CloudbedsV3.activeIntervals) {
      CloudbedsV3.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on confirmation page then executes the passed handler function
   * @param handler
   * (not usually modified)
   */
  addConfirmationPageListener: (handler) => {
    const timeout = waitForSelectors(
      CloudbedsV3.selectors.confirmationPage.waitFor,
      handler
    );
    if (CloudbedsV3.activeIntervals) {
      CloudbedsV3.activeIntervals.push(timeout);
    }
  },
  /** listen for cancellation number
   * @param handler
   */
  addCancellationPageListener: () => {
    if (CloudbedsV3.addCancellationhandler) {
      const timeout = waitForSelectors(
        // modify selectors used based on the BE's cancellation implementation
        CloudbedsV3.selectors.cancellation.cancellationTarget,
        CloudbedsV3.addCancellationhandler
      );
      if (CloudbedsV3.activeIntervals) {
        CloudbedsV3.activeIntervals.push(timeout);
      }
    }
  },
  /**
   * submit cancellation to
   *    /api/v1/submit_booking_engine_cancellation
   */
  addCancellationhandler: () => {
    const confirmationNumber = document.querySelector(
      CloudbedsV3.selectors.cancellation.confirmationNumber
    ).textContent;
    const cancellationNumber = document.querySelector(
      CloudbedsV3.selectors.cancellation.cancellationNumber
    ).textContent;
    const today = moment().format("YYYY-MM-DD");

    const submitCancellation = () => {
      if (cancellationNumber) {
        // confirmation_number used to find reservation record in our db
        // cancellation_number is arbitrary
        // in some cases, they are the same
        const cancellationData = {
          confirmation_number: confirmationNumber,
          cancellation_number: cancellationNumber,
          cancellation_date: today,
          hotel_identifier: window.gb_data.widget.identifier,
        };
        axios
          .post(
            `${guestbookSites[environment as string].rewardsdash.url
            }/api/v1/submit_booking_engine_cancellation`,
            cancellationData,
            {
              headers: {
                "Content-Type": "application/json",
                "Access-Control-Allow-Origin": "*",
              },
            }
          )
          .then((response) => {
            if (response.data.success) {
              const element = document.querySelector(
                CloudbedsV3.selectors.confirmationPage
                  .confirmationMessageContainer
              );
              if (!!element) {
                element.style.display = "none";
              }
            } else {
              console.log(response);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    };

    submitCancellation();
    // if click/touchstart events needed for sending cancellation
    // document
    //   .querySelector(ExampleBookingEngine.selectors.cancellationModal.waitFor)
    //   .addEventListener("click", () => submitCancellation());
  },
  /**
   * Creates widget container
   * (not usually modified)
   */
  createWidgetContainer: () => {
    document.querySelector(CloudbedsV3.selectors.widget.widget)?.remove();
    if (!document.querySelector(CloudbedsV3.selectors.widget.widget)) {
      const messageTarget = document.querySelector(
        CloudbedsV3.selectors.widget.target
      );
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = CloudbedsV3.selectors.widget.widget.substr(1);
        messageTarget.appendChild(injectedElement);
      }
    }
  },
  /**
   * Creates mobile widget container, usually at page head
   * (not usually modified)
   */
  createWidgetMobileContainer: () => {
    document.querySelector(CloudbedsV3.selectors.widget.widget)?.remove();
    if (!document.querySelector(CloudbedsV3.selectors.widget.widget)) {
      const messageTarget = document.querySelector(
        CloudbedsV3.selectors.widget.mobileWidgetTarget
      );
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = CloudbedsV3.selectors.widget.widget.substr(1);
        injectedElement.style.position = "relative";
        messageTarget.insertAdjacentElement("afterend", injectedElement);
      }
    }
  },
  /**
   * Mainly to handle widget scroll behavior
   * Best case, widget reanchors itself to page head underneath navbar on scroll
   * Otherwise it can stay anchored on the page bottom on mobile
   * reference Synxis for scroll and Guestline/Guestdesk for bottom anchored
   */
  additionalWidgetFunctions: () => {
    document.head.insertAdjacentHTML(
      "beforeend",
      `<style id="gb-override-styles" type="text/css">
          #guestbook-widget-desktop {
            font-family: sans-serif !important;
          }
          .gb-login-modal {
            font-family: sans-serif !important;
          }
      </style>`
    );

    if (window.innerWidth <= 768) {
      document.addEventListener("scroll", (e) => {
        if (
          document.querySelector(
            CloudbedsV3.selectors.widget.mobileWidgetFixedTarget
          )
        ) {
          document.querySelector(CloudbedsV3.selectors.widget.widget)?.remove();
          if (
            !document.querySelector(CloudbedsV3.selectors.widget.stickyWidget)
          ) {
            const messageTarget = document.querySelector(
              CloudbedsV3.selectors.widget.mobileWidgetFixedTarget
            );
            const messageTargetHeight = messageTarget.offsetHeight;
            if (messageTarget) {
              const injectedElement = document.createElement("div");
              injectedElement.id = CloudbedsV3.selectors.widget.stickyWidget.substr(
                1
              );
              // anchor widget to page botton
              injectedElement.setAttribute(
                "style",
                `
                  position: fixed;
                  z-index: 1000;
                  left: 0px;
                  width: 100%;
                  bottom: 0px;
                `
              );
              messageTarget.insertAdjacentElement("afterend", injectedElement);
            }
          }
          // re-show the non sticky mobile banner
        } else {
          document
            .querySelector(CloudbedsV3.selectors.widget.stickyWidget)
            ?.remove();
          if (!document.querySelector(CloudbedsV3.selectors.widget.widget)) {
            const messageTarget = document.querySelector(
              CloudbedsV3.selectors.widget.mobileWidgetTarget
            );
            if (messageTarget) {
              const injectedElement = document.createElement("div");
              injectedElement.id = CloudbedsV3.selectors.widget.widget.substr(
                1
              );
              messageTarget.insertAdjacentElement("afterend", injectedElement);
            }
          }
        }
      });
    }
  },
  /**
   * Creates enrollment checkbox message container
   * (not usually modified)
   */
  createEnrollmentMessageContainer: () => {
    if (
      !document.querySelector(
        CloudbedsV3.selectors.paymentPage.enrollmentMessageContainer
      )
    ) {
      const enrollmentTarget = document.querySelector(
        CloudbedsV3.selectors.paymentPage.enrollmentTarget
      );
      let messageTarget;
      if (enrollmentTarget) {
        messageTarget =
          enrollmentTarget.parentElement.parentElement.parentElement
            .parentElement.parentElement.parentElement;
      }
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = CloudbedsV3.selectors.paymentPage.enrollmentMessageContainer.substr(
          1
        );
        messageTarget.appendChild(injectedElement);
      }
    }
  },
  /**
   * Creates confirmation message container
   * (not usually modified)
   */
  createConfirmationMessageContainer: () => {
    if (
      !document.querySelector(
        CloudbedsV3.selectors.confirmationPage.confirmationMessageContainer
      )
    ) {
      const messageTarget = document.querySelector(
        CloudbedsV3.selectors.confirmationPage.messageTarget
      );
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = CloudbedsV3.selectors.confirmationPage.confirmationMessageContainer.substr(
          1
        );
        messageTarget.insertAdjacentElement("afterend", injectedElement);
      }
    }
  },
  /**
   * Set locale used for messaging translations using
   */
  setLocale: () => {
    window.gb_data.I18n.locale = CloudbedsV3.getLanguage();
  },
  /**
   * @return subtotal to use to calculate rewards and display on enrollment
   * can use in other functions to pull payment age subtotal
   */
  displaySubtotal: () => {
    const taxedRate = window.gb_data.hotel.taxedRate;
    if (document.querySelector(CloudbedsV3.selectors.confirmationPage.waitFor)) {
      return (
        parsePrice(
          document.querySelector(
            CloudbedsV3.selectors.confirmationPage.confirmationSubTotal
          )?.textContent
        ) / taxedRate
      );
    }

    if (
      document.querySelector(CloudbedsV3.selectors.paymentPage.mobileSubtotal)
    ) {
      return parsePrice(
        document.querySelector(CloudbedsV3.selectors.paymentPage.mobileSubtotal)
          .textContent
      );
    }

    let subtotal = 0;
    const subs = document.querySelectorAll(
      CloudbedsV3.selectors.paymentPage.subtotal
    );
    subs.forEach((sub: any) => {
      subtotal += Number(sub.innerText.replace(/[^\d.-]/g, ""));
    });

    const result = Number((subtotal / taxedRate).toFixed(2));
    return result;
  },
  /**
   * Triggers session storage reservation details save
   * can add additional listeners if needed
   */
  savePaymentPageInfo: () => {
    const saveReservationData = (): void => {
      if (window.location.pathname.includes("/guests")) {
        const resData = CloudbedsV3.getReservationData();
        sessionStorage.setItem("gbBookingDetails", JSON.stringify(resData));
      }
    };
    document
      .querySelectorAll(CloudbedsV3.selectors.paymentPage.saveInfoTarget)
      .forEach((btn) => {
        btn.addEventListener("click", () => saveReservationData());
      });
  },
  /**
   * Clears all listeners
   * (not usually modified)
   */
  clearAllListeners: () => {
    if (!CloudbedsV3.activeIntervals) return;
    let length = CloudbedsV3.activeIntervals.length;
    for (let i = 0; i < length; i++) {
      let int = CloudbedsV3.activeIntervals.pop();
      clearInterval(int);
    }
  },
  /**
   * Injects integration
   * (not usually modified)
   */
  injectIntegration: () => {
    handleIntegrationInjector(CloudbedsV3);
  },
  /**
   * CSS Overrides for rates page
   */
  additionalRatesPageFunctions: () => {
    if (
      document.querySelectorAll("#gb-override-styles-rates-page").length === 0
    ) {
      document.head.insertAdjacentHTML(
        "beforeend",
        `<style id="gb-override-styles-rates-page type="text/css">
          .gb-banner {
            display: flex;
          }
          .icon-container {
            top: 0px;
            align-self: center;
          }
        </style>`
      );
    }
  },
  // We'll need to add margin for the enrollment message
  additionalPaymentsPageFunctions: () => {
    // ...
  },
  // Widget still shows up on the confirmation page and needs to be removed
  additionalConfirmationPageFunctions: () => {
    if (
      document.querySelectorAll("#gb-override-styles-confirmation-page")
        .length === 0
    ) {
      document.head.insertAdjacentHTML(
        "beforeend",
        `<style id="gb-override-styles-confirmation-page" type="text/css">
          #guestbook-widget-desktop, #guestbook-widget {
            display: none;
          }
          .confirmation-container{
            margin-top: 16px;
            border-radius: 8px;
          }
        </style>`
      );
    }
  },
};

export default CloudbedsV3;
